
import Vue from "vue";
import Drawer from "./components/Drawer";
import Bar from "./components/Bar";
import ConfirmDialog from "./components/ConfirmDialog";
import ErrorDialog from "./components/Dialogs/ErrorDialog";
import ChatbaseWidget from "@/components/ChatbaseWidget/ChatbaseWidget.vue";
import { mapState, mapMutations } from "vuex";
import { Hub } from "aws-amplify/utils";

import { getCurrentUser } from "aws-amplify/auth";
import { sessionCheck, logout } from "@/auth/client";

export default Vue.extend({
  name: "App",
  components: {
    Drawer,
    Bar,
    ConfirmDialog,
    ErrorDialog,
    ChatbaseWidget
  },
  data: () => ({
    auth: !!localStorage.getItem("jwt"),
    userEmail: localStorage.getItem("user") || null,
    adminId: localStorage.getItem("adminId") || null,
    isLoadingAuth: false,
    drawerOpen: typeof window === "undefined" ? true : window.innerWidth > 960
  }),
  created() {
    // No initial auth check here - router guard handles it
    // No forced sign out here

    // --- Setup Amplify Hub listener ---
    // eslint-disable-next-line no-console
    console.log("App.vue created: Setting up Hub listener...");
    Hub.listen("auth", async ({ payload }) => {
      // eslint-disable-next-line no-console
      console.log("🚨 App.vue Hub listener: Received event:", payload.event);
      // eslint-disable-next-line no-console
      console.log("🚨 App.vue Hub listener: Full payload:", payload);

      switch (payload.event) {
        case "signedIn":
          // eslint-disable-next-line no-console
          console.log("🚨 Hub event: signedIn detected");
          await (this as any).handleSignIn();
          break;
        case "signInWithRedirect":
          // eslint-disable-next-line no-console
          console.log("🚨 Hub event: signInWithRedirect detected");
          await (this as any).handleSignIn();
          break;
        case "signedOut":
          // eslint-disable-next-line no-console
          console.log("🚨 Hub event: signedOut detected");
          (this as any).handleSignOutState();
          break;
        case "signInWithRedirect_failure":
          // eslint-disable-next-line no-console
          console.error("🚨 Hub signInWithRedirect_failure:", payload.data);
          (this as any).handleSignOutState();
          if (this.$route.path !== "/login") {
            this.$router.push("/login?error=auth_failed");
          }
          break;
        case "tokenRefresh":
          // eslint-disable-next-line no-console
          console.log("🚨 Hub tokenRefresh event");
          break;
        case "tokenRefresh_failure":
          // eslint-disable-next-line no-console
          console.error("🚨 Hub tokenRefresh_failure");
          (this as any).handleSignOutState();
          if (this.$route.path !== "/login") {
            this.$router.push("/login?error=token_refresh_failed");
          }
          break;
        default:
          // eslint-disable-next-line no-console
          console.log("🚨 Hub listener: Unknown event:", payload.event);
      }
    });
    // --- End Hub Listener Setup ---

    // Listen for manual auth success events from AuthCallback
    this.$on("authSuccess", async ({ session, currentUser }: any) => {
      // eslint-disable-next-line no-console
      console.log("🎯 App.vue: Manual authSuccess event received");
      // eslint-disable-next-line no-console
      console.log("🎯 App.vue: Manual auth session:", session);
      // eslint-disable-next-line no-console
      console.log("🎯 App.vue: Manual auth user:", currentUser);

      try {
        (this as any).handleSignInSuccess(session, currentUser);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("🎯 App.vue: Error handling manual auth success:", error);
      }
    });

    // Listen for force auth check events
    this.$root.$on("forceAuthCheck", () => {
      // eslint-disable-next-line no-console
      console.log("🔄 App.vue: Force auth check event received");
      (this as any).checkAndUpdateAuthState();
    });

    // T2-6344: logout handlers (Drawer / top bar) emit this so App.vue clears
    // its reactive `auth` flag. Password/direct-login admins have no Cognito
    // session, so the Amplify Hub `signedOut` event never fires for them and
    // the Drawer/Bar (gated on `auth`) stayed visible after logout.
    this.$root.$on("appSignOut", () => {
      // eslint-disable-next-line no-console
      console.log("🔄 App.vue: appSignOut event received");
      (this as any).handleSignOutState();
    });

    // Also listen for any auth events that might be missed
    setTimeout(async () => {
      // eslint-disable-next-line no-console
      console.log("🔍 App.vue: Checking auth state after 2 seconds...");
      (this as any).checkAndUpdateAuthState();
    }, 2000);
  },
  methods: {
    ...mapMutations(["callSnackbar"]),
    async checkAndUpdateAuthState() {
      try {
        const jwt = localStorage.getItem("jwt");
        const user = localStorage.getItem("user");
        const adminId = localStorage.getItem("adminId");

        // eslint-disable-next-line no-console
        console.log("🔍 App.vue: localStorage check:", {
          hasJWT: !!jwt,
          hasUser: !!user,
          hasAdminId: !!adminId
        });

        if (jwt && user && adminId && !this.auth) {
          // eslint-disable-next-line no-console
          console.log(
            "🔍 App.vue: Found auth data in localStorage, updating UI state..."
          );
          this.auth = true;
          this.userEmail = user;
          this.adminId = adminId;
          // eslint-disable-next-line no-console
          console.log("🔍 App.vue: Auth state updated from localStorage");
        }

        // Also try session check
        const session = await sessionCheck();
        // eslint-disable-next-line no-console
        console.log("🔍 App.vue: Session check result:", session);
        // eslint-disable-next-line no-console
        console.log("🔍 App.vue: Session details:", {
          hasTokens: !!session.tokens,
          hasIdToken: !!session.tokens?.idToken,
          isAuthenticated: !!session.tokens?.idToken && !this.auth
        });

        if (session.tokens?.idToken && !this.auth) {
          // eslint-disable-next-line no-console
          console.log(
            "🔍 App.vue: Found valid session but not authenticated in app - triggering handleSignIn"
          );
          await (this as any).handleSignIn();
        } else if (!session.tokens?.idToken) {
          // eslint-disable-next-line no-console
          console.log("🔍 App.vue: No valid tokens found in session check");
        } else {
          // eslint-disable-next-line no-console
          console.log("🔍 App.vue: Already authenticated or no tokens");
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        // eslint-disable-next-line no-console
        console.log(
          "🔍 App.vue: Session check error (normal if not logged in):",
          message
        );
      }
    },
    async handleSignIn() {
      if (localStorage.getItem("adminApiJwt")) {
        // eslint-disable-next-line no-console
        console.log(
          "🔐 handleSignIn: Skipping — admin API JWT already present (password login)"
        );
        return;
      }
      // eslint-disable-next-line no-console
      console.log("🔐 handleSignIn: Starting authentication process...");
      try {
        // eslint-disable-next-line no-console
        console.log("🔐 handleSignIn: Fetching auth session...");
        const session = await sessionCheck();
        // eslint-disable-next-line no-console
        console.log("🔐 handleSignIn: Session received:", session);

        // eslint-disable-next-line no-console
        console.log("🔐 handleSignIn: Getting current user...");
        const currentUser = await getCurrentUser();
        // eslint-disable-next-line no-console
        console.log("🔐 handleSignIn: Current user received:", currentUser);

        (this as any).handleSignInSuccess(session, currentUser);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("🔐 handleSignIn: Error handling sign in state:", error);
        (this as any).handleSignOutState();
      }
    },
    handleSignInSuccess(session: any, currentUser: any) {
      // eslint-disable-next-line no-console
      console.log("✅ handleSignInSuccess: Processing sign in...");
      // eslint-disable-next-line no-console
      console.log("✅ handleSignInSuccess: Session:", session);
      // eslint-disable-next-line no-console
      console.log("✅ handleSignInSuccess: Current user:", currentUser);

      const idToken = session.tokens?.idToken?.toString();
      const email = currentUser.signInDetails?.loginId;
      const userId = currentUser.userId;

      // eslint-disable-next-line no-console
      console.log("✅ handleSignInSuccess: Extracted data:", {
        idToken: idToken ? "Token present" : "No token",
        email: email,
        userId: userId
      });

      if (idToken && email && userId) {
        // eslint-disable-next-line no-console
        console.log(
          `✅ handleSignInSuccess: Storing user ${email}, id ${userId}`
        );
        // Cognito id token must NOT replace adminApiJwt — uploadImage needs API JWT.
        localStorage.setItem("cognitoIdToken", idToken);
        if (!localStorage.getItem("adminApiJwt")) {
          localStorage.setItem("jwt", idToken);
        }
        localStorage.setItem("user", email);
        localStorage.setItem("adminId", userId);

        this.auth = true;
        this.userEmail = email;
        this.adminId = userId;

        // eslint-disable-next-line no-console
        console.log(
          "✅ handleSignInSuccess: Auth state updated, checking redirect..."
        );

        const redirectPath = this.$route.query.redirect as string;
        // eslint-disable-next-line no-console
        console.log(
          `✅ handleSignInSuccess: Current route query redirect: ${redirectPath}`
        );
        if (redirectPath) {
          // eslint-disable-next-line no-console
          console.log(
            `✅ handleSignInSuccess: Navigating to redirect path: ${redirectPath}`
          );
          this.$router.push(redirectPath);
        } else if (this.$route.path !== "/home" && this.$route.path !== "/") {
          // eslint-disable-next-line no-console
          console.log(
            `✅ handleSignInSuccess: Navigating to default root path.`
          );
          this.$router.push("/");
        }

        this.$emit("logined");
        // eslint-disable-next-line no-console
        console.log(
          "✅ handleSignInSuccess: Authentication completed successfully!"
        );
      } else {
        const error = new Error(
          "Missing ID token, user email, or userId after sign in."
        );
        // eslint-disable-next-line no-console
        console.error("❌ handleSignInSuccess Error:", error);
        // eslint-disable-next-line no-console
        console.error("❌ Missing data:", {
          hasIdToken: !!idToken,
          hasEmail: !!email,
          hasUserId: !!userId
        });
        (this as any).handleSignOutState();
        throw error;
      }
    },
    handleSignOutState() {
      // eslint-disable-next-line no-console
      console.log("handleSignOutState: Clearing auth state and localStorage.");
      localStorage.removeItem("jwt");
      localStorage.removeItem("adminApiJwt");
      localStorage.removeItem("cognitoIdToken");
      localStorage.removeItem("user");
      localStorage.removeItem("adminId");
      this.auth = false;
      this.userEmail = null;
      this.adminId = null;
      this.isLoadingAuth = false;
    },
    async triggerSignOut() {
      try {
        await logout();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error signing out: ", error);
        (this as any).handleSignOutState();
        this.$router.push("/login?error=signout_failed");
      }
    },
    close() {
      // @ts-ignore
      this.callSnackbar({ visible: false, color: "primary", text: "" });
    }
  },
  computed: {
    ...mapState(["SnackHandler", "confirmDialogShow", "ErrorDialogShow"]),
    isMobileLayout(): boolean {
      return !!(
        this.$vuetify &&
        this.$vuetify.breakpoint &&
        this.$vuetify.breakpoint.smAndDown
      );
    },
    isSupportAssistantRoute(): boolean {
      return this.$route.path === "/support-assistant";
    },
    chatbaseUserContext() {
      if (!this.auth || !this.adminId) return null;
      return {
        userId: this.adminId,
        metadata: {
          email: this.userEmail,
          role: "admin"
        }
      };
    }
  },
  watch: {
    isMobileLayout(isMobile: boolean) {
      this.drawerOpen = !isMobile;
    },
    auth(isAuthenticated: boolean) {
      if (isAuthenticated) {
        this.drawerOpen = !(this as any).isMobileLayout;
      }
    }
  }
});
